<!--
 * @Description: 移动端创作弹窗组件
 * @Date: 2026-01-12
-->
<template>
    <Teleport to="body">
        <Transition name="slide-up">
            <div
                v-if="visible"
                class="mobile-creation-modal lg:hidden"
            >
                <!-- 顶部导航栏 -->
                <header class="modal-header">
                    <!-- 关闭按钮（向下箭头） -->
                    <button
                        type="button"
                        class="close-btn"
                        @click="handleClose"
                    >
                        <svg
                            class="w-5 h-5 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M6 9l6 6 6-6"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>

                    <!-- 工具选择器 -->
                    <div class="flex-1 flex items-center justify-center">
                        <MobileToolSelectorInModal
                            :currentTool="currentTool"
                            @change="handleToolChange"
                            @navigate="handleNavigate"
                        />
                    </div>

                    <!-- 占位元素保持居中 -->
                    <div class="w-8"></div>
                </header>

                <!-- 内容区域 -->
                <div class="modal-content">
                    <!-- 文生图设置 -->
                    <ToolTextToImgSetting
                        v-if="currentTool === 'text-to-image'"
                        :type="'text_to_image'"
                        v-model:edit_model="textToImageForm.model"
                        v-model:edit_prompt="textToImageForm.prompt"
                        v-model:edit_style="textToImageForm.style"
                        v-model:edit_aspect_ratio="textToImageForm.aspect_ratio"
                        v-model:edit_output_image_number="textToImageForm.output_image_number"
                        v-model:edit_model_list="textToImageForm.model_list"
                        v-model:batch_generate="textToImageForm.batch_generate"
                        v-model:public_visible="textToImageForm.public_visible"
                        v-model:copyright_protection="textToImageForm.copyright_protection"
                        v-model:image_size="textToImageForm.image_size"
                        v-model:use_web_search="textToImageForm.use_web_search"
                        v-model:generate_type="textToImageForm.generate_type"
                        :create_btn_disable="isCreating || !textToImageForm.prompt.trim()"
                        :create_loading="isCreating"
                        :recommend_list="recommendList"
                    />

                    <!-- 图生图设置 -->
                    <ToolImgToImgSetting
                        v-else-if="currentTool === 'image-to-image'"
                        :type="'image_to_image'"
                        v-model:edit_model="imgToImgForm.model"
                        v-model:edit_prompt="imgToImgForm.prompt"
                        v-model:edit_image_list="imgToImgForm.image_list"
                        v-model:edit_aspect_ratio="imgToImgForm.aspect_ratio"
                        v-model:edit_output_image_number="imgToImgForm.output_image_number"
                        v-model:edit_model_list="imgToImgForm.model_list"
                        v-model:batch_generate="imgToImgForm.batch_generate"
                        v-model:public_visible="imgToImgForm.public_visible"
                        v-model:copyright_protection="imgToImgForm.copyright_protection"
                        v-model:image_size="imgToImgForm.image_size"
                        v-model:use_web_search="imgToImgForm.use_web_search"
                        v-model:generate_type="imgToImgForm.generate_type"
                        :create_btn_disable="isCreating"
                        :create_loading="isCreating"
                        :recommend_list="recommendList"
                    />

                    <!-- 文生视频设置 -->
                    <ToolTextToVideoSetting
                        v-else-if="currentTool === 'text-to-video'"
                        :type="'text_to_video'"
                        v-model:edit_model="textToVideoForm.model"
                        v-model:edit_prompt="textToVideoForm.prompt"
                        v-model:edit_style="textToVideoForm.style"
                        v-model:edit_aspect_ratio="textToVideoForm.aspect_ratio"
                        v-model:edit_output_image_number="textToVideoForm.output_image_number"
                        v-model:duration="textToVideoForm.duration"
                        v-model:video_size="textToVideoForm.video_size"
                        v-model:negative_prompt="textToVideoForm.negative_prompt"
                        v-model:generate_audio="textToVideoForm.generate_audio"
                        v-model:model_version="textToVideoForm.model_version"
                        v-model:public_visible="textToVideoForm.public_visible"
                        v-model:copyright_protection="textToVideoForm.copyright_protection"
                        :create_btn_disable="isCreating || !textToVideoForm.prompt.trim()"
                        :create_loading="isCreating"
                        :recommend_list="recommendList"
                    />

                    <!-- 图生视频设置 -->
                    <ToolImageToVideoSetting
                        v-else-if="currentTool === 'image-to-video'"
                        :type="'image_to_video'"
                        v-model:edit_model="imgToVideoForm.model"
                        v-model:edit_prompt="imgToVideoForm.prompt"
                        v-model:edit_aspect_ratio="imgToVideoForm.aspect_ratio"
                        v-model:edit_output_image_number="imgToVideoForm.output_image_number"
                        v-model:duration="imgToVideoForm.duration"
                        v-model:video_size="imgToVideoForm.video_size"
                        v-model:negative_prompt="imgToVideoForm.negative_prompt"
                        v-model:image_url="imgToVideoForm.image_url"
                        v-model:image_list="imgToVideoForm.image_list"
                        v-model:image_type="imgToVideoForm.image_type"
                        v-model:frame_end_mode="imgToVideoForm.frame_end_mode"
                        v-model:first_image_url="imgToVideoForm.first_image_url"
                        v-model:last_image_url="imgToVideoForm.last_image_url"
                        v-model:model_version="imgToVideoForm.model_version"
                        v-model:generate_audio="imgToVideoForm.generate_audio"
                        :create_btn_disable="isCreating"
                        :create_loading="isCreating"
                        :recommend_list="recommendList"
                    />

                    <!-- 音乐生成设置 -->
                    <ToolTextToMusicSetting
                        v-else-if="currentTool === 'text-to-music'"
                        :type="'text_to_music'"
                        v-model:mode="textToMusicForm.mode"
                        v-model:description="textToMusicForm.description"
                        v-model:styles="textToMusicForm.styles"
                        v-model:lyrics="textToMusicForm.lyrics"
                        v-model:referenceAudio="textToMusicForm.referenceAudio"
                        v-model:referenceAudioUrl="textToMusicForm.referenceAudioUrl"
                        v-model:referenceAudioName="textToMusicForm.referenceAudioName"
                        v-model:gender="textToMusicForm.gender"
                        v-model:public_visible="textToMusicForm.public_visible"
                        v-model:copyright_protection="textToMusicForm.copyright_protection"
                        :create_btn_disable="isCreating"
                        :create_loading="isCreating"
                    />

                    <!-- 其他工具暂时显示提示 -->
                    <div
                        v-else-if="['ai-tools', 'photo-effects', 'video-effects', 'video-to-video'].includes(currentTool)"
                        class="flex flex-col items-center justify-center h-64 text-gray-400"
                    >
                        <p class="text-lg mb-2">该功能即将上线</p>
                        <p class="text-sm">敬请期待</p>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- 背景遮罩 -->
        <Transition name="fade">
            <div
                v-if="visible"
                class="modal-backdrop lg:hidden"
                @click="handleClose"
            ></div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useCreationModalStore, type ToolType } from '~/stores/creationModal'
import { useCreateStore } from '~/stores/create'
import { useUserStore } from '~/stores/user'
import { useAppStore } from '~/stores/app'
import { showLoginModal, emitter, resetRecommendLoading, emitOpenGoodsModal } from '~/utils/emitter'
import { recommendPromptPromptApi, createTextToMusicApi } from '~/api/create'
import { noticeMessagePopup } from '~/utils/common'
import { useI18n } from '~/composables/useI18n'

const router = useRouter()
const { t } = useI18n()

const creationModalStore = useCreationModalStore()
const createStore = useCreateStore()
const userStore = useUserStore()
const appStore = useAppStore()

const { visible, currentTool, initialData } = storeToRefs(creationModalStore)
const isLoggedIn = computed(() => userStore.isLoggedIn)

// 创建状态
const isCreating = ref(false)

// AI 推荐列表
const recommendList = ref<any[]>([])

// 获取 AI 推荐提示词
const getRecommendPrompt = async () => {
    recommendList.value = []
    try {
        let requestData: any = {
            type: 'IMAGE',
        }
        
        // 根据当前工具类型构建请求参数
        if (currentTool.value === 'text-to-image') {
            requestData = {
                aspect_ratio: textToImageForm.aspect_ratio,
                model: textToImageForm.model,
                prompt: textToImageForm.prompt,
                type: 'IMAGE',
            }
        } else if (currentTool.value === 'image-to-image') {
            requestData = {
                aspect_ratio: imgToImgForm.aspect_ratio,
                media_url: imgToImgForm.image_list?.[0] || '',
                model: imgToImgForm.model,
                prompt: imgToImgForm.prompt,
            }
        } else if (currentTool.value === 'text-to-video') {
            requestData = {
                aspect_ratio: textToVideoForm.aspect_ratio,
                duration: textToVideoForm.duration,
                model: textToVideoForm.model,
                prompt: textToVideoForm.prompt,
            }
        } else if (currentTool.value === 'image-to-video') {
            requestData = {
                aspect_ratio: imgToVideoForm.aspect_ratio,
                duration: imgToVideoForm.duration,
                model: imgToVideoForm.model,
                prompt: imgToVideoForm.prompt,
                media_url: imgToVideoForm.image_url,
            }
        }
        
        const res: any = await recommendPromptPromptApi(requestData)
        if (res.status === 200) {
            recommendList.value = res.data?.recommend_prompts || []
        } else if ([1005, 4001, 4002].includes(res.status)) {
            showLoginModal()
        }
    } catch (error) {
        console.error('[MobileCreationModal] getRecommendPrompt error:', error)
    } finally {
        resetRecommendLoading()
    }
}

// ==================== 创建任务 ====================

// 创建成功后的处理（关闭弹窗并跳转到历史页面）
const handleCreateSuccess = () => {
    creationModalStore.close()
    router.push('/history')
}

const handleClickCreate = async () => {
    // 根据当前工具类型调用不同的创建逻辑
    switch (currentTool.value) {
        case 'text-to-image':
            await handleCreateTextToImage()
            break
        case 'image-to-image':
            await handleCreateImageToImage()
            break
        case 'text-to-video':
            await handleCreateTextToVideo()
            break
        case 'image-to-video':
            await handleCreateImageToVideo()
            break
        case 'text-to-music':
            await handleCreateTextToMusic()
            break
        default:
            console.warn('Unsupported tool type:', currentTool.value)
    }
}

// 文生图创建
const handleCreateTextToImage = async () => {
    if (!textToImageForm.prompt.trim()) {
        noticeMessagePopup(t('common.pleaseInputPrompt'), t('common.tip'), 'warning', 5000, '')
        return
    }

    try {
        isCreating.value = true
        const data = {
            aspect_scale: textToImageForm.aspect_ratio,
            image_number: textToImageForm.output_image_number,
            model: textToImageForm.generate_type === 'batch'
                ? textToImageForm.model_list.join(',')
                : textToImageForm.model,
            prompt: textToImageForm.prompt,
            resolution: textToImageForm.image_size,
            source_type: 'NEW_WERYA',
            public_visible: textToImageForm.public_visible,
            copyright_protection: textToImageForm.copyright_protection,
            use_web_search: textToImageForm.use_web_search,
            create_type: 'TEXT_TO_IMG_BATCH',
            type: 'image',
        }
        await createStore.handleCreateImageOrVideo(data)
        // 成功后跳转到历史页面
        handleCreateSuccess()
    } catch (error: any) {
        noticeMessagePopup(error?.message || t('common.createFailed'), '', 'error', 5000, '')
    } finally {
        isCreating.value = false
    }
}

// 图生图创建
const handleCreateImageToImage = async () => {
    if (!imgToImgForm.prompt.trim()) {
        noticeMessagePopup(t('common.pleaseInputPrompt'), t('common.tip'), 'warning', 5000, '')
        return
    }
    if (!imgToImgForm.image_list.length) {
        noticeMessagePopup(t('common.pleaseInputImage'), t('common.tip'), 'warning', 5000, '')
        return
    }

    try {
        isCreating.value = true
        const data: any = {
            aspect_ratio: imgToImgForm.aspect_ratio,
            image_number: imgToImgForm.output_image_number,
            img_urls: imgToImgForm.image_list,
            prompt: imgToImgForm.prompt,
            resolution: imgToImgForm.image_size,
            source_type: 'NEW_WERYA',
            public_visible: imgToImgForm.public_visible,
            copyright_protection: imgToImgForm.copyright_protection,
            use_web_search: imgToImgForm.use_web_search,
            create_type: 'IMG_EDIT_BATCH',
            type: 'image',
        }
        if (imgToImgForm.generate_type === 'batch') {
            data.model = imgToImgForm.model_list.join(',')
        } else {
            data.model = imgToImgForm.model
        }
        data.single_img_edit = imgToImgForm.generate_type === 'single'
        await createStore.handleCreateImageOrVideo(data)
        // 成功后跳转到历史页面
        handleCreateSuccess()
    } catch (error: any) {
        noticeMessagePopup(error?.message || t('common.createFailed'), '', 'error', 5000, '')
    } finally {
        isCreating.value = false
    }
}

// 文生视频创建
const handleCreateTextToVideo = async () => {
    if (!textToVideoForm.prompt.trim()) {
        noticeMessagePopup(t('common.pleaseInputPrompt'), t('common.tip'), 'warning', 5000, '')
        return
    }

    try {
        isCreating.value = true
        const data: any = {
            aspect_ratio: textToVideoForm.aspect_ratio,
            aspect_scale: textToVideoForm.aspect_ratio,
            duration: textToVideoForm.duration || undefined,
            generate_audio: textToVideoForm.generate_audio,
            prompt: textToVideoForm.prompt,
            resolution: textToVideoForm.video_size,
            t: textToVideoForm.duration || undefined,
            video_num: textToVideoForm.output_image_number,
            video_model: textToVideoForm.model_version || textToVideoForm.model,
            source_type: 'NEW_WERYA',
            public_visible: textToVideoForm.public_visible,
            copyright_protection: textToVideoForm.copyright_protection,
            create_type: 'text_to_video',
            type: 'video',
        }
        if (textToVideoForm.negative_prompt?.trim()) {
            data.negative_prompt = textToVideoForm.negative_prompt
        }
        await createStore.handleCreateImageOrVideo(data)
        // 成功后跳转到历史页面
        handleCreateSuccess()
    } catch (error: any) {
        noticeMessagePopup(error?.message || t('common.createFailed'), '', 'error', 5000, '')
    } finally {
        isCreating.value = false
    }
}

// 图生视频创建
const handleCreateImageToVideo = async () => {
    if (!imgToVideoForm.prompt.trim()) {
        noticeMessagePopup(t('common.pleaseInputPrompt'), t('common.tip'), 'warning', 5000, '')
        return
    }
    if (!imgToVideoForm.image_url && !imgToVideoForm.frame_end_mode && imgToVideoForm.image_type === 'single_frame') {
        noticeMessagePopup(t('common.pleaseInputImage'), t('common.tip'), 'warning', 5000, '')
        return
    }

    try {
        isCreating.value = true
        const data: any = {
            aspect_ratio: imgToVideoForm.aspect_ratio,
            aspect_scale: imgToVideoForm.aspect_ratio,
            duration: imgToVideoForm.duration || undefined,
            image: imgToVideoForm.image_url,
            prompt: imgToVideoForm.prompt,
            resolution: imgToVideoForm.video_size,
            t: imgToVideoForm.duration || undefined,
            video_num: imgToVideoForm.output_image_number,
            video_model: imgToVideoForm.model_version || imgToVideoForm.model,
            source_type: 'NEW_WERYA',
            create_type: 'image_to_video',
        }
        if (imgToVideoForm.negative_prompt?.trim()) {
            data.negative_prompt = imgToVideoForm.negative_prompt
        }
        data.generate_audio = imgToVideoForm.generate_audio

        // 根据图片类型设置不同的参数
        if (imgToVideoForm.image_type === 'single_frame' && imgToVideoForm.frame_end_mode) {
            if (!imgToVideoForm.first_image_url || !imgToVideoForm.last_image_url) {
                noticeMessagePopup(t('tools.pleaseInputFirstAndLastImage'), t('common.tip'), 'warning', 5000, '')
                isCreating.value = false
                return
            }
            data.end_pic_url = imgToVideoForm.last_image_url
            data.images = [imgToVideoForm.first_image_url, imgToVideoForm.last_image_url]
            data.type = 'fist_last_to_video'
        } else if (imgToVideoForm.image_type === 'single_frame' && !imgToVideoForm.frame_end_mode) {
            data.image = imgToVideoForm.image_url
            data.type = 'single_to_video'
        } else if (imgToVideoForm.image_type === 'multiple_frames') {
            if (!imgToVideoForm.image_list.length) {
                noticeMessagePopup(t('common.pleaseInputImage'), t('common.tip'), 'warning', 5000, '')
                isCreating.value = false
                return
            }
            data.images = imgToVideoForm.image_list
            data.type = 'multi_to_video'
        }

        await createStore.handleCreateImageOrVideo(data)
        // 成功后跳转到历史页面
        handleCreateSuccess()
    } catch (error: any) {
        noticeMessagePopup(error?.message || t('common.createFailed'), '', 'error', 5000, '')
    } finally {
        isCreating.value = false
    }
}

// 音乐生成创建
const handleCreateTextToMusic = async () => {
    if (!textToMusicForm.description.trim()) {
        noticeMessagePopup(t('common.pleaseInputPrompt'), t('common.tip'), 'warning', 5000, '')
        return
    }

    try {
        isCreating.value = true
        const params = {
            description: textToMusicForm.description,
            type: (textToMusicForm.mode === 'vocal' ? 'VOCAL_SONG' : 'ONLY_MUSIC') as 'VOCAL_SONG' | 'ONLY_MUSIC',
            styles: Object.keys(textToMusicForm.styles).length > 0 ? textToMusicForm.styles : undefined,
            lyrics: textToMusicForm.mode === 'vocal' && textToMusicForm.lyrics ? textToMusicForm.lyrics : undefined,
            reference_audio: textToMusicForm.referenceAudioUrl || undefined,
            audio_name: textToMusicForm.referenceAudioName || undefined,
            gender: textToMusicForm.gender,
            public_visible: textToMusicForm.public_visible,
            copyright_protection: textToMusicForm.copyright_protection,
            platform: 'WEB' as const,
        }

        const res = await createTextToMusicApi(params)
        if (res.status === 200) {
            noticeMessagePopup(t('menus.textToMusicPage.taskSubmitted'), '', 'success', 5000, '')
            // 等待后端写入数据库后再刷新历史记录
            await new Promise(resolve => setTimeout(resolve, 2000))
            emitter.emit('refresh-creation-history')
            // 成功后跳转到历史页面
            handleCreateSuccess()
        } else if ([1005, 4001, 4002].includes(res.status)) {
            showLoginModal()
        } else if ([1011].includes(res.status)) {
            emitOpenGoodsModal()
        } else {
            noticeMessagePopup(res.message || t('menus.textToMusicPage.submitFailed'), '', 'error', 5000, '')
        }
    } catch (error: any) {
        noticeMessagePopup(error?.message || t('menus.textToMusicPage.createFailed'), '', 'error', 5000, '')
    } finally {
        isCreating.value = false
        userStore.getBalance()
    }
}

// ==================== 文生图表单数据 ====================
const textToImageForm = reactive({
    model: 'CHATBOT_GEMINI_3_PRO_IMAGE_PREVIEW', // 默认模型
    prompt: '',
    style: 'auto',
    aspect_ratio: '',
    output_image_number: 1,
    model_list: [] as string[],
    batch_generate: false,
    public_visible: true,
    copyright_protection: false,
    image_size: '',
    use_web_search: false,
    generate_type: 'standard',
})

// ==================== 图生图表单数据 ====================
const imgToImgForm = reactive({
    model: 'CHATBOT_GEMINI_3_PRO_IMAGE_PREVIEW', // 默认模型，与PC端保持一致
    prompt: '',
    aspect_ratio: '',
    output_image_number: 1,
    public_visible: true,
    copyright_protection: false,
    image_size: '',
    image_list: [] as string[], // 修正：使用 image_list 数组
    use_web_search: false,
    generate_type: 'standard',
    model_list: [] as string[],
    batch_generate: false,
})

// ==================== 文生视频表单数据 ====================
const textToVideoForm = reactive({
    model: 'google-veo3-1', // 默认模型，与PC端保持一致
    prompt: '',
    style: 'auto',
    aspect_ratio: '',
    output_image_number: 1,
    duration: 5,
    video_size: '720p',
    negative_prompt: '',
    generate_audio: true, // 与PC端一致，默认开启
    model_version: '',
    public_visible: true,
    copyright_protection: false,
})

// ==================== 图生视频表单数据 ====================
const imgToVideoForm = reactive({
    model: 'google-veo3-1', // 默认模型，与PC端保持一致
    prompt: '',
    aspect_ratio: '',
    output_image_number: 1,
    duration: 5,
    video_size: '720p',
    negative_prompt: '',
    image_url: '',
    image_list: [] as string[],
    image_type: 'single_frame', // 默认单图模式
    frame_end_mode: false, // Boolean 类型
    first_image_url: '',
    last_image_url: '',
    model_version: '',
    generate_audio: true, // 与PC端一致，默认开启
})

// ==================== 音乐生成表单数据 ====================
const textToMusicForm = reactive({
    mode: 'vocal' as 'vocal' | 'inst',
    description: '',
    styles: {} as Record<string, string>,
    lyrics: '',
    referenceAudio: null as File | null,
    referenceAudioUrl: '',
    referenceAudioName: '',
    gender: 'm' as 'm' | 'f',
    public_visible: true,
    copyright_protection: false,
})

// 关闭弹窗
const handleClose = () => {
    creationModalStore.close()
}

// 切换工具
const handleToolChange = (tool: ToolType) => {
    creationModalStore.switchTool(tool)
}

// 跳转到独立页面（用于没有 Setting 组件的工具）
const handleNavigate = (path: string) => {
    creationModalStore.close()
    router.push(path)
}

// 初始化模型数据
const initModelData = async () => {
    if (!appStore.modelData) {
        await appStore.getModelList()
    }
}

// 监听弹窗打开，初始化数据
watch(visible, async (newVal) => {
    if (newVal) {
        // 确保模型数据已加载
        await initModelData()
        
        // 如果有初始数据，合并到对应的表单
        if (initialData.value) {
            const data = initialData.value
            if (currentTool.value === 'text-to-image') {
                Object.assign(textToImageForm, data)
            } else if (currentTool.value === 'image-to-image') {
                Object.assign(imgToImgForm, data)
            } else if (currentTool.value === 'text-to-video') {
                Object.assign(textToVideoForm, data)
            } else if (currentTool.value === 'image-to-video') {
                Object.assign(imgToVideoForm, data)
            } else if (currentTool.value === 'text-to-music') {
                Object.assign(textToMusicForm, data)
            }
        }
    }
})

// 组件挂载时预加载模型数据并监听事件
onMounted(() => {
    initModelData()
    // 监听 AI 推荐事件
    emitter.on('recommend-prompt', getRecommendPrompt)
    // 监听生成按钮点击事件
    emitter.on('click-create', handleClickCreate)
})

// 组件卸载时移除监听
onUnmounted(() => {
    emitter.off('recommend-prompt', getRecommendPrompt)
    emitter.off('click-create', handleClickCreate)
})
</script>

<style scoped lang="less">
.mobile-creation-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #000;
    z-index: 100;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    // PC端隐藏
    @media (min-width: 1024px) {
        display: none !important;
    }
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    padding: 0 16px;
    background: #000;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }

    &:active {
        transform: scale(0.95);
    }
}

.modal-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    background: #0d0e12;

    // 隐藏滚动条但保持滚动功能
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

// 提升弹窗内 el-tooltip 的 z-index，避免被弹窗遮挡
:global(.prompt-popper.el-popper) {
    z-index: 200 !important;
}

.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;

    @media (min-width: 1024px) {
        display: none !important;
    }
}

// 滑入动画
.slide-up-enter-active {
    animation: slide-up-in 0.3s ease-out;
}

.slide-up-leave-active {
    animation: slide-up-out 0.25s ease-in;
}

@keyframes slide-up-in {
    from {
        transform: translateY(100%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes slide-up-out {
    from {
        transform: translateY(0);
        opacity: 1;
    }
    to {
        transform: translateY(100%);
        opacity: 0;
    }
}

// 淡入淡出动画
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
